// LoginErrorModal.tsx - Professional error modal for login failures
import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';

interface LoginErrorModalProps {
  open: boolean;
  onClose: () => void;
  errorType?: 'not_found' | 'no_email' | 'service_error';
}

const LoginErrorModal: React.FC<LoginErrorModalProps> = ({ open, onClose, errorType = 'not_found' }) => {
  const content = errorType === 'no_email'
    ? {
        title: "Email Required 📧",
        description: "We need your email address to create your account. Please make sure your social login provider (Facebook, Google, etc.) shares your email with us. You may need to adjust your privacy settings.",
      }
    : errorType === 'service_error'
      ? {
          title: "We Couldn't Finish Signing You In",
          description: "Your account information is safe, but the sign-in service did not respond correctly. Close this message and try again.",
        }
    : {
        title: "Sorry Bro, We Don't Know You 🤷‍♂️",
        description: "Your account isn't in our system yet. Guest.Stream is currently invite-only. Reach out to an existing creator or host to get access.",
      };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        },
      }}
    >
      {/* Close button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, py: 3, textAlign: 'center' }}>
        {/* Error Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <ErrorIcon sx={{ fontSize: 48, color: '#EF4444' }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#1a1a1a',
            mb: 2,
          }}
        >
          {content.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: '#666',
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {content.description}
        </Typography>

        {/* Action Button */}
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          fullWidth
          sx={{
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            bgcolor: '#2D2070',
            borderRadius: 2,
            '&:hover': {
              bgcolor: '#1f1750',
            },
          }}
        >
          Got It
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default LoginErrorModal;
